import React, { useRef, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Title, Tooltip } from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { ECGIcon, ECGSaturationIcon } from '../components/icons/Icons';

ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip);

const TOTAL_SECONDS = 30;
/**
 * Line smoothness (Chart.js cubic tension). Must stay in hook/memo deps below or edits here
 * will not refresh the chart until `data` or other deps change.
 * At high ECG sample rates, values between ~0 and ~0.4 often look very similar on screen.
 */
const ECG_LINE_TENSION = 0.2;

const ECGChart = ({ data, posture }) => {
  const detailedChartRef = useRef(null);
  const overviewChartRef = useRef(null);
  const [detailedChartOptions, setDetailedChartOptions] = useState({});
  const [overviewChartOptions, setOverviewChartOptions] = useState({});
  const [detailedStartTime, setDetailedStartTime] = useState(0);
  const [detailedLength, setDetailedLength] = useState(8);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [gain, setGain] = useState(10);

  useEffect(() => {
    const updateChartOptions = () => {
      if (detailedChartRef.current && overviewChartRef.current) {
        const yAxisMax = 2.5 * (10 / gain);
        const yAxisMin = -yAxisMax;
        const yAxisStepSize = 0.1 * (10 / gain);
        const yAxisMaxTicksLimit = Math.floor((yAxisMax - yAxisMin) / yAxisStepSize) + 1;

        setDetailedChartOptions({
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          elements: {
            line: {
              tension: ECG_LINE_TENSION,
              borderJoinStyle: 'miter',
              borderCapStyle: 'butt',
            },
            point: { radius: 0 },
          },
          scales: {
            x: {
              type: 'linear',
              position: 'bottom',
              min: detailedStartTime,
              max: detailedStartTime + detailedLength,
              ticks: {
                stepSize: 0.04,
                callback: (value) => value % 1 === 0 ? value : '',
                font: { size: 10 },
                color: 'rgba(0, 0, 0, 0.5)',
                autoSkip: false,
                maxTicksLimit: 201,
              },
              grid: {
                display: true,
                drawOnChartArea: true,
                color: (context) => {
                  const index = context.index;
                  if (index % 5 === 0) return 'rgba(0, 0, 0, 0.2)';
                  return 'rgba(0, 0, 0, 0.1)';
                },
                lineWidth: (context) => {
                  const index = context.index;
                  if (index % 5 === 0) return 1;
                  return 0.5;
                },
              },
              title: {
                display: true,
                text: 'Time (seconds)'
              }
            },
            y: {
              type: 'linear',
              position: 'left',
              min: yAxisMin,
              max: yAxisMax,
              ticks: {
                stepSize: yAxisStepSize,
                callback: (value) => value % 0.5 === 0 ? value.toFixed(1) : '',
                font: { size: 10 },
                color: 'rgba(0, 0, 0, 0.5)',
                autoSkip: false,
                maxTicksLimit: yAxisMaxTicksLimit,
              },
              grid: {
                display: true,
                drawOnChartArea: true,
                color: (context) => {
                  const value = context.tick.value;
                  if (value % 1 === 0) return 'rgba(0, 0, 0, 0.2)';
                  if (value % 0.5 === 0) return 'rgba(0, 0, 0, 0.15)';
                  return 'rgba(0, 0, 0, 0.1)';
                },
                lineWidth: (context) => {
                  const value = context.tick.value;
                  if (value % 1 === 0) return 1;
                  if (value % 0.5 === 0) return 0.75;
                  return 0.5;
                },
              },
              title: {
                display: true,
                text: 'Amplitude (mV)'
              }
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
        });

        setOverviewChartOptions({
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          elements: {
            line: {
              tension: ECG_LINE_TENSION,
              borderJoinStyle: 'miter',
              borderCapStyle: 'butt',
            },
            point: { radius: 0 },
          },
          scales: {
            x: {
                type: 'linear',
                position: 'bottom',
                min: 0,
                max: TOTAL_SECONDS,
                ticks: {
                  stepSize: 0.04, // 5 small squares per 0.2 seconds
                  callback: () =>  '' ,
                  autoSkip: false,
                  maxTicksLimit: 751, // (30 seconds / 0.04) + 1
                },
                grid: {
                  display: true,
                  drawOnChartArea: true,
                  color: (context) => {
                    const index = context.index;
                    if (index % 5 === 0) return 'rgba(0, 0, 0, 0.2)'; // Darker lines every 0.2 seconds
                    return 'rgba(0, 0, 0, 0.1)'; // Lighter lines for 0.04 seconds
                  },
                  lineWidth: (context) => {
                    const index = context.index;
                    if (index % 5 === 0) return 1; // Thicker lines every 0.2 seconds
                    return 0.5; // Thinner lines for 0.04 seconds
                  },
                },
              },
            y: {
              type: 'linear',
              position: 'left',
              min: -2.5,
              max: 2.5,
              ticks: {
                stepSize: 0.1,
                callback: () =>  '' ,
                autoSkip: false,
                maxTicksLimit: 51,
              },
              grid: {
                display: true,
                drawOnChartArea: true,
                color: (context) => {
                  const value = context.tick.value;
                  if (value % 1 === 0) return 'rgba(0, 0, 0, 0.2)'; // Darker lines for whole mV
                  if (value % 0.5 === 0) return 'rgba(0, 0, 0, 0.15)'; // Medium lines for half mV
                  return 'rgba(0, 0, 0, 0.1)'; // Lighter lines for 0.1mV
                },
                lineWidth: (context) => {
                  const value = context.tick.value;
                  if (value % 1 === 0) return 1; // Thicker lines for whole mV
                  if (value % 0.5 === 0) return 0.75; // Medium lines for half mV
                  return 0.5; // Thinner lines for 0.1mV
                },
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
        });
      }
    };

    updateChartOptions();
    window.addEventListener('resize', updateChartOptions);

    return () => {
      window.removeEventListener('resize', updateChartOptions);
    };
  }, [detailedStartTime, detailedLength, data, gain, ECG_LINE_TENSION]);

  const chartData = React.useMemo(() => {
    if (!data || !data.datasets || data.datasets.length === 0 || !data.datasets[0].data?.length) {
      return {
        datasets: [],
      };
    }

    const n = data.datasets[0].data.length;

    return {
      title: data.title,
      isFastRecovery: data.isFastRecovery,
      datasets: data.datasets.map(({ tension: _t, cubicInterpolationMode: _c, ...dataset }) => ({
        ...dataset,
        data: dataset.data.map((value, index) => ({
          x: (index * TOTAL_SECONDS) / n,
          y: typeof value === 'number' ? value : Number(value),
        })),
        borderColor: '#000000',
        borderWidth: 1,
        pointRadius: 0,
        cubicInterpolationMode: 'default',
        tension: ECG_LINE_TENSION,
      })),
    };
  }, [data, ECG_LINE_TENSION]);

  useEffect(() => {
    setDetailedStartTime((s) => {
      if (s + detailedLength > TOTAL_SECONDS) {
        return Math.max(0, TOTAL_SECONDS - detailedLength);
      }
      return s;
    });
  }, [detailedLength]);

  const handleMouseDown = (event) => {
    setIsDragging(true);
    handleMouseMove(event);
  };

  const handleResizeMouseDown = (event) => {
    event.stopPropagation();
    setIsResizing(true);
  };

  const handleMouseMove = (event) => {
    if ((!isDragging && !isResizing) || !overviewChartRef.current) {
      return;
    }
    const chart = overviewChartRef.current;
    const rect = chart.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const totalWidth = rect.width;

    if (isDragging) {
      let newStartTime = (x / totalWidth) * TOTAL_SECONDS - detailedLength / 2;
      newStartTime = Math.max(0, Math.min(TOTAL_SECONDS - detailedLength, newStartTime));
      setDetailedStartTime(newStartTime);
    }

    if (isResizing) {
      const HANDLE_WIDTH = 10;
      const leftPx = (detailedStartTime / TOTAL_SECONDS) * totalWidth;
      let rightPx = x - HANDLE_WIDTH;
      rightPx = Math.max(rightPx, leftPx + 1);
      let newLength = ((rightPx - leftPx) / totalWidth) * TOTAL_SECONDS;
      newLength = Math.max(1, Math.min(TOTAL_SECONDS - detailedStartTime, newLength));
      setDetailedLength(newLength);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging, isResizing, detailedStartTime, detailedLength]);

  if (!data || !data.datasets || data.datasets.length === 0 || !data.datasets[0].data?.length) {
    return <div>No data available</div>;
  }

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative', 
      backgroundColor: 'white',
      boxSizing: 'border-box'
    }}>
        {chartData.isFastRecovery &&
          <div style={{
            borderRadius: "10px",
            border: "0.5px solid var(--Gold-300, #946705)",
            background: "#FDF3CF",
            boxShadow: "1px 1px 15px 0px rgba(0, 0, 0, 0.10)",
            padding:"10px",
            display:"flex",
            alignItems: "center",
            columnGap:"10px",
            marginBottom:"10px",
            width:"238px"
          }}>
            <ECGSaturationIcon />
            <span className="subtitle-2">ECG saturation detected</span>
          </div>
        }
      {/* Gray box container */}
      <div style={{
        backgroundColor: '#f0f0f0',
        borderRadius: '8px',
        padding: '20px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#333'
        }}>
          <ECGIcon style={{ paddingRight: '20px' }} />
          <span style={{ fontWeight: 'bold',marginRight: '20px', borderRight: '1px solid #ccc', paddingRight: '20px', paddingLeft: '10px' }}>ECG</span>
          <span style={{ marginRight: '20px', borderRight: '1px solid #ccc', paddingRight: '20px' }}>Duration: 30s</span>
          <span style={{ marginRight: '20px', borderRight: '1px solid #ccc', paddingRight: '20px' }}>Posture: {posture}</span>
          <span style={{ marginRight: '20px', borderRight: '1px solid #ccc', paddingRight: '20px' }}>Perin Patch</span>
          <span style={{ marginRight: '20px', borderRight: '1px solid #ccc', paddingRight: '20px' }}>Sweep speed: 25 mm/s</span>
          <span style={{ marginRight: '20px' }}>Gain (mm/mV): </span>
          <select 
            value={gain} 
            onChange={(e) => setGain(Number(e.target.value))}
            style={{
              padding: '5px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              backgroundColor: 'white',
              fontSize: '14px'
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '10px' }}>32_38</span>
            <button style={{ marginRight: '5px' }}>&lt;</button>
            <button>&gt;</button>
          </div>
        </div>

        {/* Detailed Chart */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          padding: '20px', 
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ height: '300px' }}>
            <Line ref={detailedChartRef} data={chartData} options={detailedChartOptions} />
          </div>
        </div>

        {/* Overview Chart */}
        <span style={{ fontWeight: 'bold', paddingLeft: '10px' }}>Timeframe</span>
        <div style={{ height: '100px', position: 'relative' }}>
          <Line
            ref={overviewChartRef}
            data={chartData}
            options={overviewChartOptions}
          />
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: `${(detailedStartTime / TOTAL_SECONDS) * 100}%`,
              width: `${(detailedLength / TOTAL_SECONDS) * 100}%`,
              height: '70%',
              cursor: 'grab',
              backgroundColor: 'rgba(219, 120, 120, 0.15)',
            }}
            onMouseDown={handleMouseDown}
          >
            {/* Left handle */}
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '4px',
              height: '100%',
              backgroundColor: '#DB7878',
              cursor: 'ew-resize',
            }}></div>
            
            <div style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '4px',
              height: '100%',
              backgroundColor: '#DB7878',
              borderRadius: '0 8px 8px 0',
            }}></div>

            <div
              role="separator"
              aria-orientation="vertical"
              onMouseDown={handleResizeMouseDown}
              style={{
                position: 'absolute',
                right: '-10px',
                top: '10px',
                width: '10px',
                height: '50px',
                backgroundColor: '#DB7878',
                cursor: 'ew-resize',
                borderRadius: '0 8px 8px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon icon={faChevronRight} style={{ color: 'white', marginLeft: '-3px' }} size="sm" />
            </div>

            {/* Top border */}
            <div style={{
              position: 'absolute',
              left: '4px',
              right: '4px',
              top: 0,
              height: '2px',
              backgroundColor: '#DB7878',
            }}></div>
            
            {/* Bottom border */}
            <div style={{
              position: 'absolute',
              left: '4px',
              right: '4px',
              bottom: 0,
              height: '2px',
              backgroundColor: '#DB7878',
            }}></div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div style={{ 
        marginTop: '20px',
        padding: '12px', 
        backgroundColor: '#fff3cd', 
        borderRadius: '8px',
        border: '1px solid #ffc107',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px'
      }}>
        <span style={{ 
          fontSize: '20px', 
          color: '#856404',
          lineHeight: '1.2'
        }}>⚠️</span>
        <span style={{ 
          fontWeight: 'normal', 
          margin: 0, 
          fontSize: '14px',
          color: '#856404',
          lineHeight: '1.5'
        }}>
          P-waves and T-waves may appear reduced or absent due to chest-based electrode placement. Do not use this waveform alone for diagnosis. Obtain a standard 12-lead ECG if clinically indicated.
        </span>
      </div>
    </div>
  );
};

export default ECGChart;